import { useCallback, useEffect, useRef, useState } from 'react';
import cardInsertImage from '../../assets/TapSwipeInsert.png';
import style from '../payment-card/style.module.scss';
import MessageBoxSet from '../message-box-set';
import { useStore } from '../../lib/hooks/useStore';
import { ViewState, PaymentError } from '../../lib/types/paymentCardState';
import Confirm from '../confirm';
import MainMessageBox from '../../features/main-message-box/main-message-box';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import logger from '../../lib/helpers/logger';
import BalancesKvpList from '../balances-kvp-list';
import { PaymentCardServerProps } from '../../lib/typegen/payment-card-server-props';
import { v4 as guid } from 'uuid';
import EMVDevice, { KioEMVTransactionParams, KioEMVResult } from '../../lib/devices/emv-device/EMVDevice';
import { AppEnvironment } from '../../lib/typegen/app-environment';
import MessageBox from '../message-box';
import { useSessionCleanup } from '../../lib/hooks/useSessionCleanup';
import { LegacyPostAcceptedPayment, LegacyPostRejectedPayment } from '../../lib/types/legacyAdminApi';
import passKeyHelper from '../../lib/helpers/passKey';
import enumHelper from '../../lib/helpers/enums';
import api from '../../lib/api/api';
import { PaymentType } from '../../lib/typegen/payment-type';
import ServiceFeeView from '../service-fee-view';
import { parseAmountToNumber } from '../../lib/helpers/math';
import { PaymentFeeViewServerProps } from '../../lib/typegen/payment-fee-view-server-props';
import { PPConfigType } from '../../lib/typegen/pp-config-type';

function PaymentCardPaygo({
	imageAltKey,
	paymentTotalLabelKey,
	infoBoxLabelKey,
	warningBoxLabelKey,
	cardErrorGenericLabelKey,
	processingPaymentLabelKey,
	tryAgainMessageLabelKey,
	tryAgainNoLabelKey,
	tryAgainYesLabelKey,
	paymentFeeViewServerProps,
}: PaymentCardServerProps) {
	const [currentState, setCurrentState] = useState<ViewState>(ViewState.LOADING);
	const [error, setError] = useState<PaymentError | null>(null);
	const [errorHint, setErrorHint] = useState<string | null>(null);
	const emvDeviceRef = useRef<EMVDevice | null>(null);
	const isPreparingTransaction = useRef(false);
	const hasHandledCompletionRef = useRef(false);
	const PAYMENT_CARD_REGISTRY_KEY = 'payment-card-paygo';

	const {
		accountStore: {
			cartItems,
			cartTotalAmount,
			cardType,
			setPaymentType,
			transactionFee,
			setTransactionFee,
			hasPreparedTransaction,
			setHasPreparedTransaction,
			payorInformation,
		},
		sessionStore: { legacyId },
		kioskStore: { kioskId, companyId },
		uiStore: { handleNavigation },
		configStore: { getString, environment, cisType, ppType, kioskLocation, payGoClientSettings, myUsageClientSettings },
	} = useStore();
	const feeAmount = transactionFee ? parseAmountToNumber(transactionFee) : 0;
	const totalAmountWithFee = (cartTotalAmount ?? 0) + feeAmount;
	const isMyUsage = ppType === PPConfigType.MyUsage;
	const cardPresentPaymentUrl = isMyUsage
		? (myUsageClientSettings?.cardPresentPaymentUrl ?? '')
		: (payGoClientSettings?.cardPresentPaymentUrl ?? '');
	const cardPresentApiKey = isMyUsage ? (myUsageClientSettings?.cardPresentApiKey ?? '') : (payGoClientSettings?.cardPresentApiKey ?? '');
	const payGoDrawerIdentifier = payGoClientSettings?.drawerIdentifier ?? '';

	const mapCreditCardResponseToKioResult = useCallback(
		(raw: Record<string, unknown>): KioEMVResult => {
			const success = typeof raw.success === 'boolean' ? raw.success : false;
			const amountAsNumber = typeof raw.amount === 'number' ? raw.amount : totalAmountWithFee;
			const messages = Array.isArray(raw.messages) ? raw.messages : [];
			const joinedMessages = messages.map((message) => String(message)).join(' | ');
			const statusMessage = typeof raw.statusMessage === 'string' ? raw.statusMessage : '';
			const fallbackError = joinedMessages || statusMessage;

			return {
				TransType: 0,
				Result: success ? 0 : 1,
				Amount: amountAsNumber.toString(),
				Tax: '',
				ReferenceID: typeof raw.transactionId === 'string' ? raw.transactionId : '',
				AuthorizationCode: typeof raw.authorizationCode === 'string' ? raw.authorizationCode : '',
				Errors: fallbackError,
				ErrorCode: typeof raw.responseCode === 'string' ? raw.responseCode : '',
				ExpiryDate: '',
				PrimaryAccountNumber: typeof raw.maskedCardNumber === 'string' ? raw.maskedCardNumber : '',
				CardHashToken: '',
				AcqRefData: '',
				ProcessData: '',
				ReceiptData: [],
				CardReference: typeof raw.transactionId === 'string' ? raw.transactionId : '',
				RawData: raw,
				CardType: typeof raw.cardLogo === 'string' ? raw.cardLogo : '',
				StoredCredentialToken: '',
			};
		},
		[totalAmountWithFee]
	);

	const handlePaymentCompletion = useCallback(
		async (result: KioEMVResult, source: 'callback' | 'direct-api') => {
			if (hasHandledCompletionRef.current) {
				logger.logGeneral.warn({}, 'Ignoring duplicate PayGo completion event', { source, result });
				return;
			}

			hasHandledCompletionRef.current = true;
			setCurrentState(ViewState.PROCESSING);

			const item = cartItems[0];
			if (!item) {
				logger.logGeneral.error({}, 'PayGo completion missing cart item', { source, result });
				setError({ message: getString(cardErrorGenericLabelKey) || 'No account selected' });
				setCurrentState(ViewState.ERROR);
				return;
			}

			if (!item.customer) {
				item.customer = { customerId: item.item.itemNumber, name: item.item.itemNumber };
			}

			const accountNumber = item.item.itemNumber;

			if (result.Result === 0) {
				const legacyPostAcceptedPayment: LegacyPostAcceptedPayment = structuredClone({
					dynatouchGUID: legacyId!,
					firstCashTransaction: false,
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					paymentGatewayExternalID: item.item.itemNumber,
					paymentDateTime: new Date().toISOString(),
					paymentAmount: totalAmountWithFee,
					paymentType: 'Card',
					paymentGUID: guid(),
					passKey: await passKeyHelper.generatePassKey(),
					cashOnes: 0,
					cashTwos: 0,
					cashFives: 0,
					cashTens: 0,
					cashTwenties: 0,
					cashFifties: 0,
					cashHundreds: 0,
					cisProvider: enumHelper.getCisConfigTypeLabel(cisType),
					paymentGateway: enumHelper.getPpConfigTypeLabel(ppType),
					cisProviderExternalID: accountNumber,
					payorName: item.customer.name!,
				});

				api.legacyBpkAdmin.payments.postAcceptedPayment(legacyPostAcceptedPayment).catch((postErr) => {
					logger.logGeneral.error({}, 'Failed to post accepted payment:', postErr);
				});

				logger.logGeneral.log({}, 'Payment successful', {
					source,
					referenceId: result.ReferenceID,
					authCode: result.AuthorizationCode,
					amount: result.Amount,
					cardType: result.CardType,
				});
				handleNavigation('payment-complete');
				return;
			}

			const legacyPostRejectedPayment: LegacyPostRejectedPayment = structuredClone({
				customerID: companyId.toString(),
				kioskID: kioskId.toString(),
				billpaySessionGUID: legacyId!,
				rejectedECID: '',
				rejectedReason: 'Card payment error',
				rejectedPaymentType: 'Card',
				paymentAmount: totalAmountWithFee,
				payorName: item.customer.name!,
				paymentDateTime: new Date().toISOString(),
				passKey: await passKeyHelper.generatePassKey(),
			});

			api.legacyBpkAdmin.payments.postRejectedPayment(legacyPostRejectedPayment).catch((postErr) => {
				logger.logGeneral.error({}, 'Failed to post rejected payment:', postErr);
			});

			logger.logGeneral.error({}, 'Payment failed:', {
				source,
				errors: result.Errors,
				errorCode: result.ErrorCode,
				result,
			});
			setError({ message: getString(cardErrorGenericLabelKey) || result.Errors || 'Card payment error' });
			setErrorHint(getString('card-read-hint'));
			setCurrentState(ViewState.ERROR);
		},
		[cartItems, cardErrorGenericLabelKey, cisType, companyId, getString, handleNavigation, kioskId, legacyId, ppType, totalAmountWithFee]
	);

	useEffect(() => setPaymentType(cardType === 1 ? PaymentType.DebitCard : PaymentType.CreditCard), [setPaymentType, cardType]);

	const startPayGoSequence = useCallback(async () => {
		if (environment === AppEnvironment.Demo) return;

		if (!emvDeviceRef.current) {
			logger.logGeneral.error({}, 'EMV device not initialized', 'The EMV device was not able to be initialized.');
			setError({ message: getString(cardErrorGenericLabelKey) || 'Device not ready' });
			setCurrentState(ViewState.ERROR);
			return;
		}

		const accountNumber = cartItems[0]?.item?.itemNumber;
		if (!accountNumber) {
			logger.logGeneral.error({}, 'Account number not available', 'Cannot proceed with payment without account number.');
			setError({ message: getString(cardErrorGenericLabelKey) || 'Account information missing' });
			setCurrentState(ViewState.ERROR);
			return;
		}

		const accountToken = cartItems[0]?.item?.accountToken ?? '';
		if (!accountToken) {
			logger.logGeneral.error({}, 'Account token not available', {
				accountNumber,
				item: cartItems[0]?.item,
			});
			setError({ message: getString(cardErrorGenericLabelKey) || 'Account token missing' });
			setCurrentState(ViewState.ERROR);
			return;
		}

		const settingsReady = isMyUsage
			? !!cardPresentPaymentUrl && !!cardPresentApiKey
			: !!cardPresentPaymentUrl && !!cardPresentApiKey && !!payGoDrawerIdentifier;

		if (!settingsReady) {
			logger.logGeneral.error({}, 'Card-present settings missing from kiosk config', {
				ppType,
				hasUrl: !!cardPresentPaymentUrl,
				hasApiKey: !!cardPresentApiKey,
				hasDrawer: !!payGoDrawerIdentifier,
			});
			setError({ message: getString(cardErrorGenericLabelKey) || 'Card settings not configured' });
			setCurrentState(ViewState.ERROR);
			return;
		}

		hasHandledCompletionRef.current = false;
		setErrorHint(null);
		setError(null);
		setCurrentState(ViewState.PAY);

		logger.logGeneral.log({}, 'Starting card-present payment via local agent', {
			ppType,
			accountNumber,
			endpoint: cardPresentPaymentUrl,
		});

		const saleParams: KioEMVTransactionParams = {
			DeviceName: 'EMV',
			PaymentAmount: Math.round(totalAmountWithFee * 100),
			TransactionRef: guid(),
			OrderID: accountNumber,
			TokenType: cardType ?? 0,
		};

		const principalAmount = cartTotalAmount ?? 0;
		const directCardPresentRequest = isMyUsage
			? {
					accountToken,
					amount: principalAmount,
					feeAmount,
					referenceNumber: saleParams.TransactionRef ?? '',
					customField1: '',
					customField2: '',
				}
			: {
					accountToken,
					amount: totalAmountWithFee,
					referenceNumber: saleParams.TransactionRef ?? '',
					customField1: '',
					customField2: '',
					drawerIdentifier: payGoDrawerIdentifier,
				};

		try {
			const response = await fetch(cardPresentPaymentUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'x-api-key': cardPresentApiKey,
				},
				body: JSON.stringify(directCardPresentRequest),
			});

			if (!response.ok) {
				const responseText = await response.text();
				logger.logGeneral.error({}, 'Localhost card-present request failed', {
					accountNumber,
					status: response.status,
					statusText: response.statusText,
					responseText,
					request: directCardPresentRequest,
				});
				setError({ message: getString(cardErrorGenericLabelKey) || 'Card payment request failed' });
				setCurrentState(ViewState.ERROR);
				return;
			}

			const responseData: unknown = await response.json().catch(() => ({}));
			const responseObject = responseData && typeof responseData === 'object' ? (responseData as Record<string, unknown>) : {};
			const normalizedResult = mapCreditCardResponseToKioResult(responseObject);
			await handlePaymentCompletion(normalizedResult, 'direct-api');
		} catch (err) {
			logger.logGeneral.error({}, 'Localhost card-present request error', {
				accountNumber,
				request: directCardPresentRequest,
				errorMessage: err instanceof Error ? err.message : String(err),
				errorStack: err instanceof Error ? err.stack : undefined,
				error: err,
			});
			setError({ message: getString(cardErrorGenericLabelKey) || 'Card payment request failed' });
			setErrorHint(getString('card-read-hint'));
			setCurrentState(ViewState.ERROR);
			return;
		}
	}, [
		cartItems,
		cartTotalAmount,
		cardType,
		cardErrorGenericLabelKey,
		environment,
		feeAmount,
		getString,
		handlePaymentCompletion,
		isMyUsage,
		cardPresentApiKey,
		cardPresentPaymentUrl,
		payGoDrawerIdentifier,
		ppType,
		totalAmountWithFee,
		mapCreditCardResponseToKioResult,
	]);

	const prepareTransaction = useCallback(async () => {
		if (cartItems.length === 0) return;

		if (hasPreparedTransaction) {
			setCurrentState(ViewState.SERVICE_FEE);
			return;
		}

		if (isPreparingTransaction.current) return;

		isPreparingTransaction.current = true;

		const prepareTransactionRequest = {
			accountsToPay: cartItems.map((item) => ({
				accountNumber: item.item.itemNumber,
				amount: item.amountToPay,
			})),
			paymentType: 'CREDIT',
			location: kioskLocation,
			totalAmount: cartTotalAmount,
			payorInfo: payorInformation,
		};

		try {
			const data = await api.payments.prepareTransaction(prepareTransactionRequest);
			const feeString = cardType === 1 ? data?.fees?.debitCard?.fees : data?.fees?.creditCard?.fees;

			if (feeString) {
				setTransactionFee(feeString);
			}

			setHasPreparedTransaction(true);
			setCurrentState(ViewState.SERVICE_FEE);
		} catch (prepareError) {
			logger.logGeneral.error({}, 'PayGo prepare transaction failed', prepareError);
			setHasPreparedTransaction(false);
			setError({ message: getString(cardErrorGenericLabelKey) || 'Unable to load payment fees' });
			setCurrentState(ViewState.ERROR);
		} finally {
			isPreparingTransaction.current = false;
		}
	}, [
		hasPreparedTransaction,
		cartItems,
		kioskLocation,
		cartTotalAmount,
		payorInformation,
		cardType,
		setTransactionFee,
		setHasPreparedTransaction,
		getString,
		cardErrorGenericLabelKey,
	]);

	useEffect(() => {
		if (!emvDeviceRef.current) {
			emvDeviceRef.current = new EMVDevice('EMV');
		}

		window.OnKioEMVTransactionCompleted = async (result: KioEMVResult) => {
			await handlePaymentCompletion(result, 'callback');
		};
	}, [handlePaymentCompletion]);

	useSessionCleanup(
		PAYMENT_CARD_REGISTRY_KEY,
		useCallback(async () => {
			logger.logGeneral.warn({}, 'Cleaning up incomplete PayGo EMV payment session');
			try {
				emvDeviceRef.current?.cancelTransaction();
			} catch (cleanupError) {
				logger.debug.error('Error cancelling EMV Device during cleanup', cleanupError);
			}
		}, [])
	);

	useEffect(() => {
		if (environment === AppEnvironment.Demo) {
			return;
		}

		if (cartItems.length === 0) {
			logger.logGeneral.error({}, 'No cart items found', 'PaymentCardPaygo');
			setError({ message: getString(cardErrorGenericLabelKey) || 'No account selected' });
			setCurrentState(ViewState.ERROR);
			return;
		}

		prepareTransaction();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (environment === AppEnvironment.Demo && currentState === ViewState.PAY) {
			const showPayTimer = setTimeout(() => {
				setCurrentState(ViewState.PROCESSING);
				const processingTimer = setTimeout(() => {
					handleNavigation('payment-complete');
				}, 10000);
				return () => clearTimeout(processingTimer);
			}, 5000);

			return () => clearTimeout(showPayTimer);
		}
	}, [currentState, environment, handleNavigation]);

	const handleRetry = () => {
		setError(null);
		setCurrentState(ViewState.PAY);
		startPayGoSequence();
	};

	const handleCancel = () => {
		setError(null);
		handleNavigation('payment-method');
	};

	const handleServiceFeeConfirm = () => {
		setCurrentState(ViewState.PAY);
		startPayGoSequence();
	};

	const handleServiceFeeCancel = () => {
		handleNavigation('payment-method');
	};

	const renderPayView = () => {
		const item = cartItems[0];
		if (item && !item.customer) {
			item.customer = { customerId: item.item.itemNumber, name: item.item.itemNumber };
		}

		return (
			<section className={style.section}>
				<img className={style.section__image} src={cardInsertImage} alt={imageAltKey} />
				<BalancesKvpList
					className={style.section__details}
					kvpList={[{ label: getString(paymentTotalLabelKey ?? ''), value: totalAmountWithFee }]}
				/>
				<MessageBoxSet
					className={style.section__messages}
					infoText={getString(infoBoxLabelKey ?? '')}
					warningText={getString(warningBoxLabelKey ?? '')}
				/>
			</section>
		);
	};

	const renderServiceFeeView = () => (
		<ServiceFeeView
			amount={cartTotalAmount ?? 0}
			fee={feeAmount}
			onConfirm={handleServiceFeeConfirm}
			onCancel={handleServiceFeeCancel}
			labels={
				paymentFeeViewServerProps
					? paymentFeeViewServerProps
					: ({ titleLabelKey: 'service-fee-description-card' } as PaymentFeeViewServerProps)
			}
		/>
	);

	const renderProcessingView = () => (
		<section className={style.processing}>
			<div className={style.processing__spinner}></div>
			<h2 className={style.processing__title}>{getString(processingPaymentLabelKey)}</h2>
		</section>
	);

	const renderErrorView = () => (
		<section className={style.error}>
			<MainMessageBox icon={AppIconType.Error} message={error?.message ?? getString(cardErrorGenericLabelKey)} />
			<Confirm
				titleLabel={getString(tryAgainMessageLabelKey)}
				yesButton={{ label: tryAgainYesLabelKey ?? getString('yes'), value: 'yes', target: '' }}
				noButton={{ label: tryAgainNoLabelKey ?? getString('no'), value: 'no', target: '' }}
				yesCallback={handleRetry}
				noCallback={handleCancel}
			/>
			{errorHint && <MessageBox type='warning' dangerouslySetInnerHTML={{ __html: errorHint ? errorHint : getString('card-read-hint') }} />}
		</section>
	);

	const renderCurrentView = () => {
		switch (currentState) {
			case ViewState.LOADING:
				return renderProcessingView();
			case ViewState.SERVICE_FEE:
				return renderServiceFeeView();
			case ViewState.PAY:
				return renderPayView();
			case ViewState.PROCESSING:
				return renderProcessingView();
			case ViewState.ERROR:
				return renderErrorView();
			default:
				return renderPayView();
		}
	};

	return <section className={style.section}>{renderCurrentView()}</section>;
}

export default PaymentCardPaygo;
